import { Link } from 'react-router-dom';

const features = [
  {
    icon: 'ri-database-2-line',
    title: 'Product Database',
    description: 'Access a comprehensive, regularly updated spreadsheet cataloging thousands of products across all major categories, making product discovery fast and effortless.',
  },
  {
    icon: 'ri-search-eye-line',
    title: 'QC Research',
    description: 'Utilize detailed quality control data embedded within the spreadsheet to evaluate products before purchase, reducing the risk of receiving substandard items.',
  },
  {
    icon: 'ri-scales-3-line',
    title: 'Price Comparison',
    description: 'Compare prices across different categories and sellers directly within the spreadsheet, helping you identify the best deals without manual cross-referencing.',
  },
  {
    icon: 'ri-ship-line',
    title: 'Shipping Information',
    description: 'Access integrated shipping cost estimates, delivery timelines, and carrier comparisons to plan your international shipping strategy effectively.',
  },
  {
    icon: 'ri-book-open-line',
    title: 'Buying Tutorials',
    description: 'Follow step-by-step guides designed for beginners, covering everything from account setup to placing your first order through the Basetao platform.',
  },
  {
    icon: 'ri-shield-check-line',
    title: 'Security Tips',
    description: 'Learn safe purchasing practices, platform verification methods, and scam prevention strategies to shop confidently using Basetao Spreadsheet resources.',
  },
];

const reviewItems = [
  { label: 'Database Quality', rating: 5 },
  { label: 'Ease of Use', rating: 5 },
  { label: 'Information Accuracy', rating: 5 },
  { label: 'Beginner Experience', rating: 4 },
];

export default function ContentSections() {
  return (
    <>
      {/* About Section */}
      <section id="about" className="bg-background-100 section-padding">
        <div className="section-container">
          <div className="max-w-3xl mx-auto text-center mb-14">
            <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium tracking-wide uppercase mb-4">
              What Is It
            </span>
            <h2 className="text-3xl md:text-4xl font-heading text-foreground-950">
              What Is Basetao Spreadsheet?
            </h2>
            <p className="mt-4 text-foreground-600 leading-relaxed">
              The <strong>Basetao Spreadsheet</strong> is a meticulously organized product database designed to help international shoppers navigate the vast selection available through the Basetao shopping agent platform. Rather than manually browsing thousands of individual product pages across fragmented sources, the spreadsheet consolidates curated product listings into one searchable, filterable document.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
            <div className="space-y-5">
              <h3 className="font-heading text-2xl text-foreground-950">How the Spreadsheet Transforms Shopping</h3>
              <p className="text-foreground-600 leading-relaxed">
                Traditional product searching on agent platforms involves endless scrolling, inconsistent naming conventions, and difficulty comparing similar items across different stores. The Basetao Spreadsheet eliminates these pain points by providing a structured, pre-researched collection of products organized by category, price range, and popularity.
              </p>
              <p className="text-foreground-600 leading-relaxed">
                Each entry in the spreadsheet includes essential details such as product links, price information, size availability, and quality indicators. This organized approach dramatically reduces research time and makes informed purchasing decisions accessible even for first-time users of shopping agents.
              </p>
              <Link
                to="/basetao-spreadsheet-guide"
                className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 font-medium text-sm transition-colors"
              >
                Read the Complete Guide <i className="ri-arrow-right-line"></i>
              </Link>
            </div>

            <div className="bg-background-50 rounded-lg border border-background-200/60 overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-primary-100/60">
                    <th className="text-left px-5 py-3 font-heading text-foreground-950 text-base">Feature</th>
                    <th className="text-left px-5 py-3 font-heading text-foreground-950 text-base">Basetao Spreadsheet</th>
                    <th className="text-left px-5 py-3 font-heading text-foreground-950 text-base">Traditional Search</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-background-200/40">
                    <td className="px-5 py-3.5 font-medium text-foreground-800">Product Discovery</td>
                    <td className="px-5 py-3.5 text-primary-600 font-medium">Advanced</td>
                    <td className="px-5 py-3.5 text-foreground-500">Slow</td>
                  </tr>
                  <tr className="border-b border-background-200/40">
                    <td className="px-5 py-3.5 font-medium text-foreground-800">Organization</td>
                    <td className="px-5 py-3.5 text-primary-600 font-medium">Structured</td>
                    <td className="px-5 py-3.5 text-foreground-500">Random</td>
                  </tr>
                  <tr className="border-b border-background-200/40">
                    <td className="px-5 py-3.5 font-medium text-foreground-800">Research Time</td>
                    <td className="px-5 py-3.5 text-primary-600 font-medium">Reduced</td>
                    <td className="px-5 py-3.5 text-foreground-500">Long</td>
                  </tr>
                  <tr>
                    <td className="px-5 py-3.5 font-medium text-foreground-800">Beginner Friendly</td>
                    <td className="px-5 py-3.5 text-primary-600 font-medium">Yes</td>
                    <td className="px-5 py-3.5 text-foreground-500">No</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="bg-background-50 section-padding">
        <div className="section-container">
          <div className="text-center mb-12">
            <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium tracking-wide uppercase mb-4">
              Why Use It
            </span>
            <h2 className="text-3xl md:text-4xl font-heading text-foreground-950">
              Why Use Basetao Spreadsheet?
            </h2>
            <p className="mt-4 text-foreground-600 max-w-2xl mx-auto text-balance">
              Discover how the spreadsheet empowers shoppers with organized data, streamlined research, and actionable insights for smarter purchasing decisions.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature) => (
              <div
                key={feature.title}
                className="group bg-background-100 rounded-lg border border-background-200/60 p-6 md:p-7 hover:border-primary-300 transition-all duration-300"
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 group-hover:bg-primary-200 transition-colors mb-4">
                  <i className={`${feature.icon} text-xl text-primary-600`}></i>
                </div>
                <h3 className="font-heading text-xl text-foreground-950 mb-2">{feature.title}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Review Section */}
      <section id="review" className="bg-background-100 section-padding">
        <div className="section-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium tracking-wide uppercase mb-4">
                Our Review
              </span>
              <h2 className="text-3xl md:text-4xl font-heading text-foreground-950">
                Basetao Spreadsheet Review
              </h2>
              <p className="mt-4 text-foreground-600 leading-relaxed">
                After extensive testing and daily use, we have evaluated the Basetao Spreadsheet across multiple dimensions. The spreadsheet consistently delivers exceptional value for shoppers at all experience levels, from beginners placing their first order to experienced resellers managing bulk purchases.
              </p>
              <p className="mt-3 text-foreground-600 leading-relaxed">
                The database quality stands out as the strongest feature, with regularly updated product listings that include accurate pricing and availability information. Navigation is intuitive even for users unfamiliar with spreadsheet tools, and the logical category structure makes finding specific product types effortless.
              </p>
            </div>

            <div className="bg-background-50 rounded-lg border border-background-200/60 p-6 md:p-8">
              <h3 className="font-heading text-xl text-foreground-950 mb-6">Rating Breakdown</h3>
              <div className="space-y-5">
                {reviewItems.map((item) => (
                  <div key={item.label}>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-sm font-medium text-foreground-800">{item.label}</span>
                      <span className="text-xs text-foreground-500">{item.rating}/5</span>
                    </div>
                    <div className="flex items-center gap-1">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <i
                          key={i}
                          className={`ri-star-fill text-sm ${i < item.rating ? 'text-accent-500' : 'text-background-300'}`}
                        ></i>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-6 pt-5 border-t border-background-200/60">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                    <span className="font-heading text-primary-600 text-lg">4.8</span>
                  </div>
                  <div>
                    <p className="font-heading text-lg text-foreground-950">Overall Rating</p>
                    <p className="text-xs text-foreground-500">Based on comprehensive evaluation</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Shipping Section */}
      <section className="bg-background-50 section-padding">
        <div className="section-container">
          <div className="text-center mb-12">
            <span className="inline-block px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium tracking-wide uppercase mb-4">
              Shipping Guide
            </span>
            <h2 className="text-3xl md:text-4xl font-heading text-foreground-950">
              Basetao Shipping Cost Guide
            </h2>
            <p className="mt-4 text-foreground-600 max-w-2xl mx-auto text-balance">
              Understanding international shipping options is essential for making cost-effective purchases through Basetao. Here is an overview of typical shipping methods and their characteristics.
            </p>
          </div>

          <div className="max-w-3xl mx-auto">
            <div className="bg-background-100 rounded-lg border border-background-200/60 overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-secondary-100/50">
                    <th className="text-left px-6 py-4 font-heading text-foreground-950 text-base">Shipping Method</th>
                    <th className="text-left px-6 py-4 font-heading text-foreground-950 text-base">Estimated Speed</th>
                    <th className="text-left px-6 py-4 font-heading text-foreground-950 text-base">Cost Level</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-background-200/40">
                    <td className="px-6 py-4 font-medium text-foreground-800">Express (DHL, FedEx, UPS)</td>
                    <td className="px-6 py-4 text-foreground-600">3-7 Business Days</td>
                    <td className="px-6 py-4">
                      <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium">High</span>
                    </td>
                  </tr>
                  <tr className="border-b border-background-200/40">
                    <td className="px-6 py-4 font-medium text-foreground-800">Air Shipping (EMS, EUB)</td>
                    <td className="px-6 py-4 text-foreground-600">7-15 Business Days</td>
                    <td className="px-6 py-4">
                      <span className="inline-block px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium">Medium</span>
                    </td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 font-medium text-foreground-800">Economy (Sea, SAL)</td>
                    <td className="px-6 py-4 text-foreground-600">20-60 Business Days</td>
                    <td className="px-6 py-4">
                      <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">Low</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Pillar Articles Section */}
      <section id="articles" className="bg-background-50 section-padding">
        <div className="section-container">
          <div className="text-center mb-12">
            <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium tracking-wide uppercase mb-4">
              In-Depth Articles
            </span>
            <h2 className="text-3xl md:text-4xl font-heading text-foreground-950">
              Basetao Spreadsheet Pillar Articles
            </h2>
            <p className="mt-4 text-foreground-600 max-w-2xl mx-auto text-balance">
              Explore our collection of comprehensive guides covering every aspect of the Basetao Spreadsheet and international shopping experience.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                path: '/basetao-spreadsheet-guide',
                title: 'Complete Guide 2026',
                desc: 'The ultimate resource covering everything from getting started to advanced spreadsheet techniques for international shoppers.',
                badge: 'Featured',
                badgeClass: 'bg-accent-100 text-accent-700',
              },
              {
                path: '/basetao-spreadsheet-review',
                title: 'Honest Review & Rating',
                desc: 'In-depth evaluation of the Basetao Spreadsheet across database quality, ease of use, accuracy, and overall value.',
                badge: 'Popular',
                badgeClass: 'bg-primary-100 text-primary-700',
              },
              {
                path: '/basetao-shipping-cost',
                title: 'Shipping Cost Guide',
                desc: 'Complete breakdown of shipping methods, estimated costs, delivery timelines, and money-saving consolidation strategies.',
                badge: 'Essential',
                badgeClass: 'bg-secondary-100 text-secondary-700',
              },
              {
                path: '/basetao-how-to-use',
                title: 'How to Use Tutorial',
                desc: 'Step-by-step beginner tutorial walking you through accessing, navigating, and mastering the spreadsheet.',
                badge: 'Guide',
                badgeClass: 'bg-primary-100 text-primary-700',
              },
              {
                path: '/basetao-warehouse-guide',
                title: 'Warehouse Guide',
                desc: 'Understanding warehouse storage, QC inspection, package consolidation, and maximizing warehouse services.',
                badge: 'Guide',
                badgeClass: 'bg-secondary-100 text-secondary-700',
              },
              {
                path: '/basetao-coupon-code',
                title: 'Coupon & Discount Code',
                desc: 'Latest Basetao coupons, discount strategies, seasonal sales timing, and tips for reducing total shopping costs.',
                badge: 'Save Money',
                badgeClass: 'bg-accent-100 text-accent-700',
              },
              {
                path: '/is-basetao-legit',
                title: 'Is Basetao Legit?',
                desc: 'Comprehensive safety review, platform trust evaluation, scam prevention tips, and secure shopping practices.',
                badge: 'Trust',
                badgeClass: 'bg-accent-100 text-accent-700',
              },
              {
                path: '/basetao-vs-superbuy',
                title: 'Basetao vs Superbuy',
                desc: 'Detailed platform comparison covering fees, features, spreadsheet advantages, shipping options, and overall value.',
                badge: 'Comparison',
                badgeClass: 'bg-secondary-100 text-secondary-700',
              },
              {
                path: '/basetao-spreadsheet-shoes',
                title: 'Shoes Database Spotlight',
                desc: 'Deep dive into the shoe category with sneaker types, sizing tips, deal hunting, and footwear buying strategies.',
                badge: 'Category',
                badgeClass: 'bg-primary-100 text-primary-700',
              },
              {
                path: '/basetao-spreadsheet-hoodies',
                title: 'Hoodies & Sweaters Guide',
                desc: 'Everything about hoodie shopping through the spreadsheet including fabric quality, styles, and seasonal strategies.',
                badge: 'Category',
                badgeClass: 'bg-secondary-100 text-secondary-700',
              },
              {
                path: '/basetao-shopping-tips',
                title: 'Shopping Tips & Tricks',
                desc: 'Expert strategies from experienced shoppers for smarter purchasing, cost optimization, and avoiding common mistakes.',
                badge: 'Expert',
                badgeClass: 'bg-primary-100 text-primary-700',
              },
            ].map((article) => (
              <Link
                key={article.path}
                to={article.path}
                className="group bg-background-100 rounded-lg border border-background-200/60 p-6 hover:border-primary-300 transition-all duration-300 flex flex-col"
              >
                <div className="flex items-center justify-between mb-3">
                  <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium ${article.badgeClass}`}>
                    {article.badge}
                  </span>
                  <i className="ri-arrow-right-line text-foreground-400 group-hover:text-primary-500 group-hover:translate-x-0.5 transition-all"></i>
                </div>
                <h3 className="font-heading text-lg text-foreground-950 group-hover:text-primary-600 transition-colors mb-2">
                  {article.title}
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed flex-1">
                  {article.desc}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Article CTA */}
      <section className="bg-primary-500 section-padding">
        <div className="section-container text-center">
          <h2 className="text-3xl md:text-4xl font-heading text-background-50">
            Want to Learn More?
          </h2>
          <p className="mt-4 text-primary-100 max-w-xl mx-auto text-balance">
            Read our in-depth guide covering everything from getting started to advanced tips for using the Basetao Spreadsheet effectively.
          </p>
          <Link
            to="/basetao-spreadsheet-guide"
            className="inline-flex items-center gap-2 mt-8 px-8 py-3.5 bg-background-50 text-primary-600 font-medium rounded-md hover:bg-background-100 transition-all duration-300 whitespace-nowrap"
          >
            Read Complete Guide <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </section>
    </>
  );
}